import {
  AlertTriangle,
  ArrowUpRight,
  BellRing,
  CircleCheck,
  Info,
} from "lucide-react";
import { useCanAccess, useGetIdentity, useGetList } from "ra-core";
import { Link } from "react-router";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { DashboardActivityLog } from "../../dashboard/DashboardActivityLog";
import type { GulaTask } from "../gulaTasks";
import type { GulaClient, GulaOrder, GulaSubscription } from "../gulaTypes";
import { currentCallSlot, useAgents } from "../useAgents";
import {
  buildAlerts,
  salesAlertsOnly,
  type AlertTone,
  type HomeAlert,
} from "./homeAlerts";
import { HomeKpiPanel, ShippingFeed } from "./HomeKpiPanel";
import { homeKpis } from "./homeKpis";
import { MyDayCard } from "../callPlan/MyDayCard";
import { MyDayPage } from "../callPlan/MyDayPage";
import { RankingCard } from "../callPlan/RankingCard";
import { TakeWorkCard } from "../callPlan/TakeWorkCard";
import { useCallPlan } from "../callPlan/useCallPlan";
import { SHIFTS } from "../shifts";
import { TeamChat } from "../team/TeamChat";
import { usePendingWelcomeCalls } from "../welcome/useWelcomeCalls";
import { HomeTasks } from "./HomeTasks";

const TONE_STYLE: Record<
  AlertTone,
  { icon: typeof Info; className: string; label: string }
> = {
  critical: {
    icon: AlertTriangle,
    className: "text-destructive",
    label: "Urgente",
  },
  warning: {
    icon: BellRing,
    className: "text-amber-600 dark:text-amber-400",
    label: "Pendiente",
  },
  info: {
    icon: Info,
    className: "text-sky-600 dark:text-sky-400",
    label: "Ahora",
  },
};

const AlertRow = ({ alert }: { alert: HomeAlert }) => {
  const tone = TONE_STYLE[alert.tone];
  const Icon = tone.icon;
  return (
    <div className="flex gap-3 py-2.5">
      <Icon
        className={cn("mt-0.5 h-4 w-4 shrink-0", tone.className)}
        aria-label={tone.label}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Link to={alert.link} className="text-sm font-medium hover:underline">
          {alert.items.length} · {alert.title}
        </Link>
        <div className="flex flex-wrap gap-x-2 text-xs text-muted-foreground">
          {alert.items.slice(0, 4).map((item, index) => (
            <Link
              key={`${item.link}-${index}`}
              to={item.link}
              className="hover:text-foreground hover:underline"
            >
              {item.label}
            </Link>
          ))}
          {alert.items.length > 4 ? (
            <span>y {alert.items.length - 4} más</span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const longDate = (date: Date) => {
  const text = date.toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/** Inicio: the team's coordination screen. */
export const HomePage = () => {
  const { identity } = useGetIdentity();
  const { agents, meId } = useAgents();
  // Setters only see the sales side: no shipping tiles, feed or alerts
  const { canAccess: canSeeOperations = false, isPending: rolePending } =
    useCanAccess({
      resource: "operations",
      action: "list",
    });
  const { data: clients = [], refetch: refetchClients } =
    useGetList<GulaClient>(
      "gula_clients_summary",
      {
        pagination: { page: 1, perPage: 2000 },
        sort: { field: "created_at", order: "DESC" },
      },
      { refetchInterval: 60_000 },
    );
  const { missions, ranking, reload } = useCallPlan();
  const reloadPlan = () => {
    reload();
    refetchClients();
  };
  const { data: orders = [] } = useGetList<GulaOrder>(
    "gula_orders_summary",
    {
      filter: { "stage@neq": "cancelled" },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "stage_changed_at", order: "ASC" },
    },
    { refetchInterval: 60_000 },
  );
  const { data: subscriptions = [] } = useGetList<GulaSubscription>(
    "gula_subscriptions",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "id", order: "ASC" },
    },
  );
  const { data: tasks = [], refetch: refetchTasks } = useGetList<GulaTask>(
    "tasks",
    {
      filter: { "done_date@is": null },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "due_date", order: "ASC" },
    },
  );

  const { data: welcomeCalls = [] } = usePendingWelcomeCalls();

  const now = new Date();
  const slot = currentCallSlot(now);
  const allAlerts = buildAlerts({
    clients,
    orders,
    subscriptions,
    welcomeCalls,
    now,
  });
  const alerts = canSeeOperations ? allAlerts : salesAlertsOnly(allAlerts);
  const firstName = identity?.fullName?.split(" ")[0] ?? "";
  const shiftLabel = slot
    ? SHIFTS.find((shift) => shift.key === slot)?.label
    : null;
  const kpis = homeKpis({ clients, orders, tasks, meId, slot, now });
  const showTasks = () =>
    document
      .getElementById("tareas")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });

  // Setters land straight on their sales panel: the day's calls in the best
  // order, the race against the team, the team chat and their tasks
  if (!rolePending && !canSeeOperations) {
    return (
      <div className="flex flex-col gap-6">
        <MyDayPage />
        <div id="tareas" className="scroll-mt-4 pb-4">
          <HomeTasks
            tasks={tasks}
            clients={clients}
            agents={agents}
            meId={meId}
            onChanged={() => refetchTasks()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 py-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">
            Hola{firstName ? `, ${firstName}` : ""}
          </h1>
          <p className="text-sm text-muted-foreground">{longDate(now)}</p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "rounded-full px-3 py-1.5 text-sm font-medium",
              shiftLabel
                ? "bg-lime-200 text-neutral-900"
                : "bg-muted text-muted-foreground",
            )}
          >
            {shiftLabel
              ? `Turno de ${shiftLabel.toLowerCase()} · ahora`
              : "Fuera de horario de llamadas"}
          </span>
          <Link
            to="/clientes"
            className="inline-flex items-center gap-1 rounded-full bg-neutral-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-neutral-800 dark:bg-lime-300 dark:text-neutral-900"
          >
            Ir al tablero
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <HomeKpiPanel
        kpis={kpis}
        slot={slot}
        onShowTasks={showTasks}
        showOperations={canSeeOperations}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className={canSeeOperations ? "lg:col-span-7" : "lg:col-span-12"}>
          <MyDayCard missions={missions} ranking={ranking} meId={meId} />
        </div>
        {canSeeOperations ? (
          <div className="lg:col-span-5">
            <ShippingFeed orders={orders} now={now} />
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <TakeWorkCard
            clients={clients}
            missions={missions}
            agents={agents}
            meId={meId}
            onChanged={reloadPlan}
          />
        </div>
        <div className="lg:col-span-5">
          <RankingCard ranking={ranking} meId={meId} onChanged={reloadPlan} />
        </div>
      </div>

      <TeamChat agents={agents} clients={clients} />

      <DashboardActivityLog />

      <section className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <BellRing className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Avisos</h2>
        </div>
        <Card className="divide-y rounded-3xl px-4 py-1">
          {alerts.length ? (
            alerts.map((alert) => <AlertRow key={alert.key} alert={alert} />)
          ) : (
            <p className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
              <CircleCheck className="h-4 w-4" />
              Todo al día.
            </p>
          )}
        </Card>
      </section>

      <div id="tareas" className="scroll-mt-4">
        <HomeTasks
          tasks={tasks}
          clients={clients}
          agents={agents}
          meId={meId}
          onChanged={() => refetchTasks()}
        />
      </div>
    </div>
  );
};
